import { useState } from 'react';
import { Popover } from '../primitives';
import type { FacetDef, FilterDict } from './types';

export interface FilterPopoverProps {
  filters: FilterDict;
  onFiltersChange: (next: FilterDict) => void;
  facets: ReadonlyArray<FacetDef>;
  /** Test-id prefix so each host page gets stable selectors (e.g. "work-items", "documents"). */
  testIdPrefix: string;
  /** Trigger button label. Defaults to "+ Filter". */
  triggerLabel?: string;
}

/**
 * `+ Filter` popover — the single mechanism for combining attribute filters.
 *
 * Two panes inside the popover body:
 *   - Root pane: facet list (one per `FacetDef`).
 *   - Value pane: multi-select checkbox list for the picked facet, with a
 *     ← back arrow that returns to the root pane (so the user can stack
 *     more facets without closing).
 *
 * The popover stays open after a value toggle. Combining facets is implicit
 * AND across facets, OR within a facet's values — same shape as the URL
 * params each host page writes.
 */
export function FilterPopover({
  filters,
  onFiltersChange,
  facets,
  testIdPrefix,
  triggerLabel = '+ Filter'
}: FilterPopoverProps) {
  return (
    <Popover
      trigger={(api) => (
        <button
          type="button"
          className="pn-btn pn-btn-sm"
          data-testid={`${testIdPrefix}-filter-trigger`}
          {...api}
        >
          {triggerLabel}
        </button>
      )}
      label="Add a filter"
      align="start"
      data-testid={`${testIdPrefix}-filter-popover`}
    >
      {() => (
        <FilterPopoverBody
          filters={filters}
          onFiltersChange={onFiltersChange}
          facets={facets}
          testIdPrefix={testIdPrefix}
        />
      )}
    </Popover>
  );
}

function FilterPopoverBody({
  filters,
  onFiltersChange,
  facets,
  testIdPrefix
}: Required<Pick<FilterPopoverProps, 'filters' | 'onFiltersChange' | 'facets' | 'testIdPrefix'>>) {
  const [activeFacet, setActiveFacet] = useState<string | null>(null);

  if (activeFacet === null) {
    return (
      <div className="pn-listctl-filter-pane" data-testid={`${testIdPrefix}-filter-pane-root`}>
        <div className="pn-listctl-filter-pane-head">Add a filter</div>
        {facets.map((f) => {
          const count = (filters[f.key] ?? []).length;
          if (f.comingSoon) {
            return (
              <div
                key={f.key}
                className="pn-listctl-filter-facet-row"
                data-testid={`${testIdPrefix}-filter-facet-${f.key}`}
                data-coming-soon="1"
                aria-disabled="true"
              >
                <span className="pn-listctl-filter-facet-row-label">{f.label}</span>
                <span className="pn-listctl-filter-facet-row-coming-soon">Coming soon</span>
              </div>
            );
          }
          return (
            <button
              key={f.key}
              type="button"
              className="pn-listctl-filter-facet-row"
              data-testid={`${testIdPrefix}-filter-facet-${f.key}`}
              onClick={() => setActiveFacet(f.key)}
            >
              <span className="pn-listctl-filter-facet-row-label">{f.label}</span>
              {count > 0 ? (
                <span className="pn-listctl-filter-facet-row-count">{count}</span>
              ) : null}
              <span className="pn-listctl-filter-facet-row-chev" aria-hidden="true">›</span>
            </button>
          );
        })}
      </div>
    );
  }

  const facet = facets.find((f) => f.key === activeFacet);
  if (!facet) {
    setActiveFacet(null);
    return null;
  }

  const selected = filters[activeFacet] ?? [];

  function toggle(valueId: string) {
    const has = selected.includes(valueId);
    const nextValues = has ? selected.filter((v) => v !== valueId) : [...selected, valueId];
    onFiltersChange({ ...filters, [activeFacet as string]: nextValues });
  }

  return (
    <div
      className="pn-listctl-filter-pane"
      data-testid={`${testIdPrefix}-filter-pane-${activeFacet}`}
    >
      <button
        type="button"
        className="pn-listctl-filter-back"
        data-testid={`${testIdPrefix}-filter-back`}
        onClick={() => setActiveFacet(null)}
      >
        <span aria-hidden="true">←</span> {facet.label}
      </button>
      {facet.options.length === 0 ? (
        <div className="pn-listctl-filter-empty">
          No {facet.label.toLowerCase()} to choose from.
        </div>
      ) : (
        facet.options.map((opt) => {
          const checked = selected.includes(opt.id);
          return (
            <label
              key={opt.id}
              className="pn-listctl-filter-value-row"
              data-testid={`${testIdPrefix}-filter-value-${activeFacet}-${opt.id}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(opt.id)}
                data-testid={`${testIdPrefix}-filter-toggle-${activeFacet}-${opt.id}`}
              />
              <span>{opt.label}</span>
            </label>
          );
        })
      )}
    </div>
  );
}
